<template>
  <div
    v-if="page"
    ref="pageEl"
    v-hotkey="shortcuts"
    class="page-inline"
    :class="{ 'page-selected': selected, editing: isEditingTitle }"
    @click.stop="onClick"
    @dblclick="openPage($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <div class="infos">
      <NotesIcon class="icon" />
      <div class="title-blocks">
        <RInput
          v-if="isEditingTitle"
          ref="inputEl"
          :value="page.title"
          placeholder="Untitled"
          class="input"
          @blur="inputBlur"
          @enter="blur"
        />
        <span v-else class="title" :class="{ isEmpty: !page.title }">{{
          page.title || "Untitled"
        }}</span>
        <div v-if="previewedBlocks" class="blocks">
          <div
            v-for="block in previewedBlocks"
            :key="block.id"
            class="block-preview"
          >
            {{ blockPreviewText(block) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  provide,
  ref,
  toRefs,
  watch,
} from "vue";

import type { Block as BlockType, Page } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import { pageActions } from "@/components/actions/actions";
import { blockToPreviewText } from "@/components/editor/utils";
import NotesIcon from "@/components/icons/NotesIcon.vue";
import * as inject from "@/components/inject";
import { getPageShortcuts } from "@/components/object-shortcuts";
import {
  assignShortcuts,
  copyTitleInlineItem,
  Shortcuts,
} from "@/components/shortcuts";
import { bindAsync, useReleaser } from "@/ref";
import { makeMove } from "@/utils/move";

import RInput from "./RInput.vue";

export default defineComponent({
  components: {
    NotesIcon,
    RInput,
  },
  props: {
    editing: Boolean,
    pageId: {
      type: String,
      required: true,
    },
    selected: Boolean,
  },
  emits: {
    quitEditing: () => true,
    selectPage: (_page: Page) => true,
    setSelectedPageEl: (_el: HTMLElement) => true,
  },
  async setup(props, { emit }) {
    const { editing, pageId, selected } = toRefs(props);
    const isEditingTitle = computed(() => editing.value && selected.value);
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const simpleModal = inject.simpleModal();
    const router = inject.router();
    const inputEl = ref<InstanceType<typeof RInput>>();
    const contextMenu = inject.contextMenu();
    const menu = inject.menu();
    const palette = inject.palette();
    const destinationPicker = inject.destinationPicker();
    const move = makeMove(controller, profile, logger, destinationPicker);
    const pageEl = ref<HTMLElement>();
    const parent = ref({ kind: "page", id: pageId });
    provide("parent", parent);
    provide("blockSelector", {
      cancel: () => {},
      disable: () => {},
      enable: () => {},
    });
    const releaser = useReleaser();
    const page = await releaser.add(
      bindAsync(pageId, (id) => Promise.resolve(controller.page.get(id)))
    );
    const inputBlur = (text: string) => {
      if (isEditingTitle.value) {
        if (page.value && text !== page.value.title) {
          controller.page.update(page.value.id, { title: text }, "app");
        }
        emit("quitEditing");
      }
    };
    const blur = () => (isEditingTitle.value ? inputEl.value?.blur() : null);
    releaser.defer(
      watch(
        () => isEditingTitle.value,
        (editing) => {
          nextTick(() => {
            if (editing && inputEl.value) {
              // Force caret placement to the end when entering edit mode.
              inputEl.value.focus(false, true);
            }
          });
        },
        { immediate: true }
      )
    );
    releaser.defer(
      watch(selected, (value) => {
        if (value && pageEl.value) emit("setSelectedPageEl", pageEl.value);
      })
    );
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      if (selected.value && page.value)
        return {
          ...getPageShortcuts(
            page.value,
            {
              controller,
              logger,
              profile,
              router,
              simpleModal,
              menu,
              move,
              palette,
              ...injection,
            },
            { closeContextMenu: contextMenu.value.hide }
          ),
          ...assignShortcuts(injection, copyTitleInlineItem, copyPageName),
        };
      else return {};
    });
    const copyPageName = () => {
      if (selected.value && !isEditingTitle.value && page.value) {
        const { id, title } = page.value;
        copy(title);
        controller.notifications.notifyRich(
          [{ page: id, title }, " copied to clipboard"],
          "info"
        );
      }
    };
    const openPage = (mouseEvent?: MouseEvent) => {
      if (page.value) {
        pageActions.open.action(
          { type: "page", value: page.value },
          { controller, router, clickEvent: mouseEvent }
        );
      }
    };
    const onClick = () => (page.value ? emit("selectPage", page.value) : null);
    const showContextMenu = (e: MouseEvent) => {
      if (page.value) {
        onClick();
        contextMenu.value.show(
          {
            type: "page",
            value: page.value,
          },
          e
        );
      }
    };
    const previewedBlocks = computed<BlockType[] | null>(
      () =>
        page.value?.notes?.blocks
          .filter((block) => block.type !== "embed" && block.type !== "divider")
          .slice(0, 2) || null
    );
    const blockPreviewText = (block: BlockType): string => {
      return blockToPreviewText(block);
    };
    return {
      blur,
      blockPreviewText,
      inputBlur,
      inputEl,
      isEditingTitle,
      onClick,
      openPage,
      page,
      pageEl,
      previewedBlocks,
      shortcuts,
      showContextMenu,
    };
  },
});
</script>

<style lang="scss" scoped>
.page-inline {
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid transparent;
  padding: 6px 16px;
  position: relative;
  border-radius: 3px;
  .infos {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  &:hover {
    @include themed() {
      border: 1px solid t($border-inline-element-hover);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }
  &.page-selected {
    @include themed() {
      border: 1px solid t($border-inline-element-selected);
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
    }
  }
  &.editing {
    @include themed() {
      background: t($bg-inline-element-editing);
      border: 1px solid t($bg-inline-element-editing);
    }
  }
  .icon {
    height: 16px;
    width: 16px;
    margin: 2px 12px auto 0;
  }
  .title-blocks {
    display: flex;
    flex-direction: column;
    row-gap: 4px;
    .title {
      @extend %font-body-regular;
      @include themed() {
        color: t($primary);
      }

      &.isEmpty {
        @include themed() {
          color: t($grey-50);
        }
      }
    }

    .input {
      outline: none;
      @extend %font-body-regular;
      height: 20px;
    }
    .blocks {
      display: flex;
      flex-direction: column;
      row-gap: 4px;
      @include themed() {
        color: t($grey-60);
      }
      .block-preview {
        @extend %font-caption-regular;
      }
    }
  }
}
</style>
